import React, { useEffect, useState } from 'react';
import toast, { Toaster } from 'react-hot-toast';
import { Plus, FileText, Eye, Settings, ListChecks, PowerOff, Power, Archive, ChevronDown, Lock, Share2, HelpCircle } from 'lucide-react';
import AdminLayout from '../../components/admin/AdminLayout';
import ToggleSwitch from '../../components/admin/ToggleSwitch';
import Modal from '../../components/admin/Modal';
import ConfigurarEnqueteForm from '../../components/admin/ConfigurarEnqueteForm';
import PerguntasBuilder from '../../components/admin/PerguntasBuilder';
import CriarEnqueteModal from '../../components/admin/CriarEnqueteModal';
import PreviewModal from '../../components/admin/PreviewModal';
import ConfirmarStatusEnqueteModal from '../../components/admin/ConfirmarStatusEnqueteModal';
import CompartilharLinkModal from '../../components/admin/CompartilharLinkModal';
import LegendaCoresModal from '../../components/admin/LegendaCoresModal';
import { adminEnquetesAPI } from '../../utils/adminEnquetesAPI';
import { adminEnqueteLegadoAPI } from '../../utils/adminEnqueteLegadoAPI';
import { sucessoComHorario } from '../../utils/toastComHorario';

const BADGE_STATUS = {
  rascunho: 'bg-gray-100 text-gray-700',
  publicada: 'bg-green-100 text-green-700',
  encerrada: 'bg-red-100 text-red-700',
  ativa: 'bg-green-100 text-green-700'
};

// Borda pulsante do card (só na aba Ativas) — sinaliza de longe em que
// estado cada enquete está, sem precisar ler o badge de status:
// - vermelha: privada e sem nenhuma pergunta (formulário vazio, esquecida)
// - laranja: privada mas já tem pergunta (pronta, só falta publicar)
// - amarela: pública em rascunho (link de prévia — ver CompartilharLinkModal)
// - verde: pública e votação aberta (status publicada)
// - azul: pública mas encerrada (arquivada, ainda visível)
// Opacidade reduzida (/45) de propósito: a cor sólida cheia competia demais
// com o resto do card — o anel pulsante (::after, ver index.css) já carrega
// a cor no espectador, a borda só precisa dar o tom de fundo.
function classeBordaStatus(enquete) {
  if (enquete.visibilidade !== 'publica') {
    return enquete.total_perguntas
      ? 'border-2 border-orange-500/45 animate-pulse-orange'
      : 'border-2 border-red-500/45 animate-pulse-red';
  }
  if (enquete.status === 'rascunho') return 'border-2 border-amber-400/45 animate-pulse-yellow';
  if (enquete.status === 'encerrada') return 'border-2 border-blue-500/45 animate-pulse-blue';
  return 'border-2 border-emerald-500/45 animate-pulse-green';
}

const GerenciarEnquetes = () => {
  const [enquetes, setEnquetes] = useState([]);
  const [legado, setLegado] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mostrarCriar, setMostrarCriar] = useState(false);
  const [previewId, setPreviewId] = useState(null);
  const [modalAberto, setModalAberto] = useState(null); // { tipo: 'configurar'|'perguntas', enqueteId }
  const [modalStatus, setModalStatus] = useState(null); // { enquete, acao: 'desativar'|'reativar' }
  const [enqueteCompartilhar, setEnqueteCompartilhar] = useState(null);
  const [mostrarLegenda, setMostrarLegenda] = useState(false);
  const [abaEnquetes, setAbaEnquetes] = useState('ativas'); // 'ativas'|'desativadas'
  const [legadoAberto, setLegadoAberto] = useState(false);
  const [idRecemCriada, setIdRecemCriada] = useState(null);

  const carregar = async () => {
    setLoading(true);
    try {
      const [lista, listaLegado] = await Promise.all([
        adminEnquetesAPI.listar(),
        adminEnqueteLegadoAPI.listar()
      ]);
      setEnquetes(lista);
      setLegado(listaLegado);
    } catch (error) {
      toast.error(error.userMessage || 'Erro ao carregar enquetes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregar();
  }, []);

  // Enquetes legadas (Portal de Entrada, Telão do Brasil) sempre aparecem na
  // aba Ativas, sempre antes das dinâmicas — não têm conceito de
  // desativação (só público/privado) nem nenhuma outra ação de gerenciador,
  // então nunca entram na aba Desativadas.
  const onToggleLegado = async (item, novoValor) => {
    setLegado((atual) => atual.map((e) => (e.chave === item.chave ? { ...e, publica: novoValor } : e)));
    try {
      await adminEnqueteLegadoAPI.atualizarVisibilidade(item.chave, novoValor);
      sucessoComHorario(novoValor ? `${item.titulo} agora está pública` : `${item.titulo} agora está privada`);
    } catch (error) {
      setLegado((atual) => atual.map((e) => (e.chave === item.chave ? { ...e, publica: !novoValor } : e)));
      toast.error(error.userMessage || 'Erro ao atualizar visibilidade');
    }
  };

  // Depois de criar, a enquete nova entra com uma animação de destaque na
  // lista (ver .animate-enquete-nova) pra chamar atenção de que ela ainda
  // precisa de imagem/perguntas — sem encadear outro modal automaticamente.
  const onEnqueteCriada = (enqueteCriada) => {
    carregar();
    setIdRecemCriada(enqueteCriada.id);
    setTimeout(() => {
      setIdRecemCriada((atual) => (atual === enqueteCriada.id ? null : atual));
    }, 4000);
  };

  const onTogglePublica = async (enquete, publica) => {
    if (publica && !enquete.total_perguntas) {
      toast.error('Adicione pelo menos uma pergunta antes de tornar a enquete pública');
      return;
    }
    // Pública + rascunho é permitido (link de prévia — ver
    // adminEnqueteController.js/atualizarEnquete): votação e resultado
    // continuam bloqueados no lado público até o status virar "Publicada".
    setEnquetes((atual) => atual.map((e) => (e.id === enquete.id ? { ...e, visibilidade: publica ? 'publica' : 'privada' } : e)));
    try {
      await adminEnquetesAPI.atualizar(enquete.id, { visibilidade: publica ? 'publica' : 'privada' });
      if (publica && enquete.status === 'rascunho') {
        toast('Enquete pública como link de prévia — votação fica liberada só quando o status virar "Publicada"', { icon: 'ℹ️' });
      }
    } catch (error) {
      setEnquetes((atual) => atual.map((e) => (e.id === enquete.id ? { ...e, visibilidade: publica ? 'privada' : 'publica' } : e)));
      if (error.response?.data?.code === 'SEM_PERGUNTAS') {
        toast.error('Adicione pelo menos uma pergunta antes de tornar a enquete pública');
      } else {
        toast.error(error.userMessage || 'Erro ao atualizar visibilidade');
      }
    }
  };

  // Sempre recarrega ao fechar — cobre o modal "Editar Perguntas"
  // (PerguntasBuilder), que não tem callback próprio de "salvou algo" e
  // por isso deixava total_perguntas desatualizado na lista depois de
  // adicionar/remover pergunta, fazendo o toggle de pública bloquear com
  // "adicione pelo menos uma pergunta" mesmo já tendo pergunta de verdade.
  const fecharModal = () => {
    setModalAberto(null);
    carregar();
  };

  const enqueteDoModal = modalAberto ? enquetes.find((e) => e.id === modalAberto.enqueteId) : null;

  // MySQL retorna BOOLEAN como número (0/1), não como boolean — comparar
  // com === false/!== false não funciona (0 !== false é true em JS).
  const enquetesAtivas = enquetes.filter((e) => Boolean(e.ativa));
  const enquetesDesativadas = enquetes.filter((e) => !e.ativa);

  return (
    <AdminLayout title="Gerenciar Enquetes">
      <Toaster position="top-center" />

      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold text-gray-900">Enquetes</h2>
        <button onClick={() => setMostrarCriar(true)} className="btn-primary flex items-center gap-2">
          <Plus className="w-4 h-4" /> Nova enquete
        </button>
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap mb-6">
        <div className="inline-flex items-center gap-1 bg-gray-100 rounded-xl p-1">
          <button
            onClick={() => setAbaEnquetes('ativas')}
            className={`flex items-center gap-1.5 text-sm font-medium px-4 py-2 rounded-lg transition-colors ${
              abaEnquetes === 'ativas' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Ativas
            <span className="text-xs font-medium px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500">{enquetesAtivas.length + legado.length}</span>
          </button>
          <button
            onClick={() => setAbaEnquetes('desativadas')}
            className={`flex items-center gap-1.5 text-sm font-medium px-4 py-2 rounded-lg transition-colors ${
              abaEnquetes === 'desativadas' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Desativadas
            <span className="text-xs font-medium px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500">{enquetesDesativadas.length}</span>
          </button>
        </div>

        <button
          onClick={() => setMostrarLegenda(true)}
          className="flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-gray-700 px-3 py-2 rounded-lg hover:bg-gray-100"
        >
          <HelpCircle className="w-4 h-4" /> O que significam as cores?
        </button>
      </div>

      {loading ? (
        <div className="loading-spinner mx-auto" />
      ) : abaEnquetes === 'ativas' ? (
        legado.length === 0 && enquetesAtivas.length === 0 ? (
          <p className="text-gray-500 text-center py-12">Nenhuma enquete ativa no momento.</p>
        ) : (
          <div className="space-y-3">
            {legado.length > 0 && (
              <div className="card p-0 overflow-hidden">
                <button
                  onClick={() => setLegadoAberto((v) => !v)}
                  className="w-full flex items-center justify-between px-6 py-4 text-left"
                >
                  <span className="font-semibold text-gray-900 flex items-center gap-2">
                    <Archive className="w-4 h-4 text-gray-400" /> Enquetes Legadas
                    <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">
                      {legado.length}
                    </span>
                  </span>
                  <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${legadoAberto ? 'rotate-180' : ''}`} />
                </button>

                {legadoAberto && (
                  <div className="border-t border-gray-100 divide-y divide-gray-100">
                    {legado.map((item) => (
                      <div key={item.chave} className="flex items-center justify-between gap-4 flex-wrap px-6 py-4">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <h3 className="font-medium text-gray-900 truncate">{item.titulo}</h3>
                            <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">legado</span>
                            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${BADGE_STATUS[item.status]}`}>
                              {item.status}
                            </span>
                          </div>
                          <p className="text-sm text-gray-500">{item.total_votos.toLocaleString('pt-BR')} voto(s) registrado(s)</p>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <span className="text-xs text-gray-400 hidden sm:inline">
                            {item.publica ? 'Pública' : 'Privada'}
                          </span>
                          <ToggleSwitch
                            ativo={item.publica}
                            onChange={(v) => onToggleLegado(item, v)}
                            label={`Tornar ${item.titulo} ${item.publica ? 'privada' : 'pública'}`}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            {enquetesAtivas.map((enquete) => {
              const previaRascunho = enquete.visibilidade === 'publica' && enquete.status === 'rascunho';
              return (
              <div
                key={enquete.id}
                className={`card flex items-center justify-between gap-4 flex-wrap ${
                  enquete.id === idRecemCriada ? 'animate-enquete-nova' : ''
                } ${classeBordaStatus(enquete)}`}
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <h3 className="font-semibold text-gray-900 truncate">{enquete.titulo}</h3>
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${BADGE_STATUS[enquete.status]}`}>
                      {enquete.status}
                    </span>
                    {previaRascunho && (
                      <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
                        pública em rascunho — só acessível por link
                      </span>
                    )}
                    {enquete.id === idRecemCriada && (
                      <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-[#40A1E6]/10 text-[#40A1E6]">
                        recém-criada — configure abaixo
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-500 flex items-center gap-1">
                    <FileText className="w-3 h-3" /> {enquete.total_perguntas} pergunta(s)
                  </p>
                </div>
                <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-400 hidden sm:inline">
                      {enquete.visibilidade === 'publica' ? 'Pública' : 'Privada'}
                    </span>
                    <ToggleSwitch
                      ativo={enquete.visibilidade === 'publica'}
                      onChange={(v) => onTogglePublica(enquete, v)}
                      label={`Tornar ${enquete.titulo} ${enquete.visibilidade === 'publica' ? 'privada' : 'pública'}`}
                    />
                  </div>

                  {previaRascunho && (
                    <button
                      onClick={() => setEnqueteCompartilhar(enquete)}
                      className="flex items-center gap-1.5 text-sm font-medium px-2.5 sm:px-3 py-2 rounded-lg border border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100"
                    >
                      <Share2 className="w-3.5 h-3.5" /> <span className="hidden xs:inline">Compartilhar Link</span>
                    </button>
                  )}
                  <button
                    onClick={() => setModalAberto({ tipo: 'configurar', enqueteId: enquete.id })}
                    className="flex items-center gap-1.5 text-sm px-2.5 sm:px-3 py-2 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50"
                  >
                    <Settings className="w-3.5 h-3.5" /> <span className="hidden xs:inline">Configurar</span>
                  </button>
                  <button
                    onClick={() => setModalAberto({ tipo: 'perguntas', enqueteId: enquete.id })}
                    className="btn-primary flex items-center gap-1.5 text-sm px-2.5 sm:px-3 py-2"
                  >
                    <ListChecks className="w-3.5 h-3.5" /> <span className="hidden xs:inline">Editar Perguntas</span>
                  </button>
                  <button
                    onClick={() => setPreviewId(enquete.id)}
                    className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-primary-600 px-2 py-2"
                    title="Ver Preview"
                  >
                    <Eye className="w-4 h-4" /> <span className="hidden xs:inline">Ver Preview</span>
                  </button>
                  <button
                    onClick={() => setModalStatus({ enquete, acao: 'desativar' })}
                    className="text-amber-600 hover:bg-amber-50 rounded-lg p-2"
                    title="Desativar enquete"
                  >
                    <PowerOff className="w-4 h-4" />
                  </button>
                </div>
              </div>
              );
            })}
          </div>
        )
      ) : enquetesDesativadas.length === 0 ? (
        <p className="text-gray-500 text-center py-12">Nenhuma enquete desativada.</p>
      ) : (
        <div className="space-y-3">
          {enquetesDesativadas.map((enquete) => (
            <div key={enquete.id} className="card flex items-center justify-between gap-4 flex-wrap opacity-75">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <h3 className="font-medium text-gray-900 truncate">{enquete.titulo}</h3>
                  {/* Cor neutralizada de propósito aqui: o verde/vermelho de
                      BADGE_STATUS sinaliza "em vigor agora", o que contradita
                      visualmente "desativada"/"privada" ao lado. O texto do
                      status continua útil (ex: estava publicada antes de ser
                      desativada), só a cor não deveria mais competir. */}
                  <span
                    className="text-xs font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-500"
                    title="Estágio antes de ser desativada — não está mais em vigor"
                  >
                    {enquete.status}
                  </span>
                  <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">
                    <PowerOff className="w-3 h-3" /> desativada
                  </span>
                  <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">
                    <Lock className="w-3 h-3" /> privada
                  </span>
                </div>
                <p className="text-sm text-gray-500 flex items-center gap-1">
                  <FileText className="w-3 h-3" /> {enquete.total_perguntas} pergunta(s)
                </p>
              </div>
              <button
                onClick={() => setModalStatus({ enquete, acao: 'reativar' })}
                className="flex items-center gap-1.5 text-sm font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded-lg px-3 py-2 transition-colors flex-shrink-0"
              >
                <Power className="w-3.5 h-3.5" /> Reativar
              </button>
            </div>
          ))}
        </div>
      )}

      {modalAberto?.tipo === 'configurar' && (
        <Modal titulo={`Configurar: ${enqueteDoModal?.titulo || ''}`} onFechar={fecharModal} maxWidth="max-w-4xl">
          <ConfigurarEnqueteForm enqueteId={modalAberto.enqueteId} onSalvo={carregar} />
        </Modal>
      )}

      {modalAberto?.tipo === 'perguntas' && (
        <Modal titulo={`Perguntas: ${enqueteDoModal?.titulo || ''}`} onFechar={fecharModal} maxWidth="max-w-2xl" rodapeFechar>
          <PerguntasBuilder enqueteId={modalAberto.enqueteId} />
        </Modal>
      )}

      {mostrarCriar && (
        <Modal titulo="Nova enquete" onFechar={() => setMostrarCriar(false)} maxWidth="max-w-lg">
          <CriarEnqueteModal
            onFechar={() => setMostrarCriar(false)}
            onCriada={onEnqueteCriada}
          />
        </Modal>
      )}

      {previewId && (
        <PreviewModal enqueteId={previewId} onFechar={() => setPreviewId(null)} />
      )}

      {enqueteCompartilhar && (
        <Modal titulo="Compartilhar link da enquete" onFechar={() => setEnqueteCompartilhar(null)} maxWidth="max-w-md" rodapeFechar>
          <CompartilharLinkModal enquete={enqueteCompartilhar} />
        </Modal>
      )}

      {mostrarLegenda && (
        <Modal
          titulo="O que significam as cores?"
          onFechar={() => setMostrarLegenda(false)}
          maxWidth="max-w-md"
          rodapeFechar
        >
          <LegendaCoresModal />
        </Modal>
      )}

      {modalStatus && (
        <Modal
          titulo={modalStatus.acao === 'desativar' ? 'Desativar enquete' : 'Reativar enquete'}
          onFechar={() => setModalStatus(null)}
          maxWidth="max-w-md"
        >
          <ConfirmarStatusEnqueteModal
            enquete={modalStatus.enquete}
            acao={modalStatus.acao}
            onFechar={() => setModalStatus(null)}
            onConfirmado={carregar}
          />
        </Modal>
      )}
    </AdminLayout>
  );
};

export default GerenciarEnquetes;
